import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ArrowDown, Moon, Sun, Sunrise } from 'lucide-react';

import { cn } from '@/lib/utils';
import { useAnimatedMount, ANIMATION_DURATION } from '@/hooks/useAnimatedMount';
import type { ChatContentBlock } from '@/hooks/useWebSocket';
import type { ChatInteraction, ChatInteractionSubmission } from '@/services/api';
import { useAuthStore } from '@/stores/authStore';
import type { LocalMessage } from '@/stores/chatStore';
import { useSettingsStore } from '@/stores/settingsStore';
import { isSupersededTrafficPerformanceSurface } from '@/components/carrierInitializationSurface';
import { ChatMessage } from './ChatMessage';

interface ChatMessageListProps {
  messages: LocalMessage[];
  isStreaming: boolean;
  streamingContent?: ChatContentBlock[];
  elapsed?: number;
  lastActivityTime?: number | null;
  provider: 'claude' | 'codex';
  /** Set when the chat belongs to another user: interactions are disabled and files are not fetched. */
  readOnlyOwnerName?: string;
  onSubmitInteraction?: (
    interaction: ChatInteraction,
    submission: ChatInteractionSubmission
  ) => Promise<boolean>;
  onInvokeCommand?: (command: string) => boolean | Promise<boolean>;
}

const SCROLL_THRESHOLD = 100;

function EmptyState() {
  const { user } = useAuthStore();
  const { userName } = useSettingsStore();
  const hour = new Date().getHours();

  let greeting: string;
  let Icon: typeof Sun;
  if (hour >= 5 && hour < 12) {
    greeting = 'Good Morning';
    Icon = Sunrise;
  } else if (hour >= 12 && hour < 17) {
    greeting = 'Good Afternoon';
    Icon = Sun;
  } else {
    greeting = 'Good Evening';
    Icon = Moon;
  }

  const displayName = userName || user?.username;
  const displayGreeting = displayName ? `${greeting}, ${displayName}` : greeting;

  return (
    <div className="flex flex-1 items-start justify-center p-8 pt-16 md:pt-24">
      <div className="max-w-md text-center">
        <Icon className="mx-auto mb-4 h-14 w-14 text-primary-500 dark:text-primary-400 md:h-20 md:w-20" />
        <h2 className="mb-2 text-base font-semibold text-surface-900 dark:text-surface-100 md:text-lg">{displayGreeting}</h2>
        <p className="text-surface-600 dark:text-surface-400">Type a message below to start chatting.</p>
      </div>
    </div>
  );
}

function ScrollToBottomButton({
  onClick,
  visible,
  hasNewMessages,
}: {
  onClick: () => void;
  visible: boolean;
  hasNewMessages?: boolean;
}) {
  const { shouldRender, isVisible } = useAnimatedMount(visible, {
    exitDuration: ANIMATION_DURATION.fast,
    enterDuration: ANIMATION_DURATION.fast,
  });
  if (!shouldRender) return null;

  return (
    <button
      onClick={onClick}
      className={cn(
        'absolute bottom-4 right-4 z-10 flex h-10 w-10 items-center justify-center rounded-full shadow-md',
        'border border-surface-200 bg-white text-surface-600 dark:border-surface-700 dark:bg-surface-800 dark:text-surface-400',
        'hover:bg-surface-50 hover:text-surface-900 dark:hover:bg-surface-700 dark:hover:text-surface-100',
        'transition-all duration-100 focus:outline-none focus:ring-2 focus:ring-primary-500/50',
        hasNewMessages && 'ring-2 ring-primary-500',
        isVisible ? 'scale-100 opacity-100' : 'scale-90 opacity-0'
      )}
      title="Scroll to bottom"
      aria-label="Scroll to bottom"
    >
      <ArrowDown className="h-5 w-5" />
      {hasNewMessages && <span className="absolute -right-1 -top-1 h-3 w-3 animate-pulse rounded-full bg-primary-500" />}
    </button>
  );
}

function useSmartAutoScroll(
  containerRef: React.RefObject<HTMLDivElement>,
  dependencies: unknown[],
  isStreaming: boolean
) {
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [hasNewMessages, setHasNewMessages] = useState(false);
  const prevDepsRef = useRef<unknown[]>(dependencies);
  const userScrolledRef = useRef(false);

  const checkIfAtBottom = useCallback(() => {
    const container = containerRef.current;
    if (!container) return true;
    return container.scrollHeight - container.scrollTop - container.clientHeight < SCROLL_THRESHOLD;
  }, [containerRef]);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    const container = containerRef.current;
    if (!container) return;
    container.scrollTo({ top: container.scrollHeight, behavior });
    setIsAtBottom(true);
    setHasNewMessages(false);
    userScrolledRef.current = false;
  }, [containerRef]);

  const handleScroll = useCallback(() => {
    const atBottom = checkIfAtBottom();
    setIsAtBottom(atBottom);
    if (atBottom) {
      setHasNewMessages(false);
      userScrolledRef.current = false;
    } else {
      userScrolledRef.current = true;
    }
  }, [checkIfAtBottom]);

  useLayoutEffect(() => {
    const depsChanged =
      dependencies.length !== prevDepsRef.current.length ||
      dependencies.some((dependency, index) => dependency !== prevDepsRef.current[index]);
    if (!depsChanged) return;
    prevDepsRef.current = dependencies;
    if (isStreaming || isAtBottom || !userScrolledRef.current) {
      requestAnimationFrame(() => scrollToBottom(isStreaming ? 'instant' : 'smooth'));
    } else {
      setHasNewMessages(true);
    }
  }, [dependencies, isAtBottom, isStreaming, scrollToBottom]);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (container) container.scrollTop = container.scrollHeight;
  }, [containerRef]);

  return { isAtBottom, hasNewMessages, scrollToBottom, handleScroll };
}

export function ChatMessageList({
  messages,
  isStreaming,
  streamingContent,
  elapsed,
  lastActivityTime,
  provider,
  readOnlyOwnerName,
  onSubmitInteraction,
  onInvokeCommand,
}: ChatMessageListProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const messageIds = messages.map((message) => message.id).join(',');
  const lastMessageContent = messages[messages.length - 1]?.content || '';
  const messageContents = messages.map((message) => message.content);
  const visibleMessages = messages.filter((_, index) => (
    !isSupersededTrafficPerformanceSurface(messageContents, index)
  ));
  const latestSubmittedServiceDefinitionInteractionId = useMemo(() => {
    for (let index = messages.length - 1; index >= 0; index -= 1) {
      const interaction = messages[index]?.interaction;
      if (
        interaction?.kind === 'service_definition_form'
        && interaction.status === 'submitted'
      ) {
        return interaction.id;
      }
    }
    return null;
  }, [messages]);
  const { isAtBottom, hasNewMessages, scrollToBottom, handleScroll } = useSmartAutoScroll(
    containerRef,
    [messageIds, lastMessageContent, messages.length],
    isStreaming
  );

  if (messages.length === 0) return <EmptyState />;

  return (
    <div className="relative flex-1 overflow-hidden">
      <div ref={containerRef} onScroll={handleScroll} className="h-full space-y-4 overflow-y-auto p-4">
        {visibleMessages.map((message) => (
          <ChatMessage
            key={message.id}
            message={message}
            streamingContent={message.isStreaming ? streamingContent : undefined}
            elapsed={message.isStreaming ? elapsed : undefined}
            lastActivityTime={message.isStreaming ? lastActivityTime : undefined}
            provider={provider}
            interactionDisabled={isStreaming || readOnlyOwnerName !== undefined}
            readOnlyOwnerName={readOnlyOwnerName}
            showCarrierDiscoveryProgress={
              message.interaction?.id === latestSubmittedServiceDefinitionInteractionId
            }
            onSubmitInteraction={onSubmitInteraction}
            onInvokeCommand={onInvokeCommand}
          />
        ))}
      </div>
      <ScrollToBottomButton
        onClick={() => scrollToBottom('smooth')}
        visible={!isAtBottom}
        hasNewMessages={hasNewMessages}
      />
    </div>
  );
}
